import { ReactNode, useId, useMemo } from "react";
import { FieldValues, useFormContext, Validate, Path } from "react-hook-form";
import { LinearProgress } from "react-admin";
import { Box, FormControl, FormHelperText, InputLabel, ListSubheader, MenuItem, Select, SxProps } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

interface CommonSelectInputProps<T extends string | number> {
  source: Path<FieldValues>;
  label: string;
  validate?: Validate<T, FieldValues>;
  sx?: SxProps;
  size?: "small" | "medium";
  fullWidth?: boolean;
  disabled?: boolean;
  testId?: string;
  renderValue?: (value: T) => ReactNode;
  warning?: false | null | string;
  required?: boolean;
  helperText?: string;
  bulkEdit?: boolean;
  defaultValue?: T;
  displayEmpty?: boolean;
}

interface SelectInputPropsWithOptions<T extends string | number> extends CommonSelectInputProps<T> {
  options?: undefined | Array<[/* value */ T, /* label */ ReactNode]>;
  groups?: never;
  optionsWithGroups?: never;
}

interface SelectInputPropsWithGroupedOptions<T extends string | number> extends CommonSelectInputProps<T> {
  groups?: Array<string>;
  optionsWithGroups?: Array<[/* value */ T, /* label */ ReactNode, /* group */ string]>;
  options?: never;
}

export function SelectInput<T extends string | number>(
  props: SelectInputPropsWithOptions<T> | SelectInputPropsWithGroupedOptions<T>,
) {
  const {
    source,
    label,
    options = [],
    optionsWithGroups = [],
    groups = [],
    validate,
    sx = {},
    size,
    fullWidth,
    disabled,
    testId,
    renderValue,
    warning,
    required,
    helperText,
    bulkEdit,
    defaultValue,
    displayEmpty,
  } = props;
  const labelId = useId();
  const { register, formState, watch, getValues, clearErrors } = useFormContext<FieldValues>();
  const { ref, onChange, ...fieldProps } = register(source, { validate: validate as any });

  const value = watch(source) ?? defaultValue ?? "";
  const error = formState.errors[source]?.message;
  const groupedOptions = useMemo(() => {
    const groupsMap = groups.reduce<Record<string, Array<[T, string | ReactNode]>>>(
      (acc, group) => ({ ...acc, [group]: [] }),
      {},
    );
    optionsWithGroups.forEach(([value, label, groupLabel]) => {
      if (!groupsMap[groupLabel]) {
        groupsMap[groupLabel] = [];
      }
      groupsMap[groupLabel].push([value, label]);
    });
    for (const group of Object.keys(groupsMap)) {
      if (groupsMap[group].length === 0) {
        delete groupsMap[group];
      }
    }
    return groupsMap;
  }, [optionsWithGroups, groups]);

  return (
    <FormControl sx={sx} data-testid={testId} fullWidth={fullWidth}>
      <InputLabel id={labelId} required={required} error={!!error}>
        {label}
      </InputLabel>
      <Select
        size={size}
        {...fieldProps}
        label={label}
        labelId={labelId}
        inputRef={ref}
        value={value}
        error={!!error}
        displayEmpty={displayEmpty}
        disabled={disabled || formState.isSubmitting}
        renderValue={renderValue}
        onChange={(event) => {
          const value = event.target.value as T;
          if (typeof validate === "function" && validate(value, { ...getValues(), [source]: value }) === true) {
            clearErrors(source);
          }
          return onChange(event);
        }}
      >
        {bulkEdit && (
          <MenuItem value="__DO_NOT_CHANGE__">
            <Box component="i" sx={{ color: "text.disabled" }}>
              nicht ändern
            </Box>
          </MenuItem>
        )}
        {options.length + optionsWithGroups.length === 0 ? (
          <LinearProgress sx={{ ml: "1em" }} />
        ) : Object.keys(groupedOptions).length > 0 ? (
          Object.entries(groupedOptions)
            .filter(([_, groupOptions]) => groupOptions.length > 0)
            .flatMap(([groupLabel, groupOptions]) => [
              <ListSubheader
                key={`header-${groupLabel}`}
                sx={{ color: "text.secondary", fontWeight: "bold", marginLeft: "-6px", lineHeight: "32px" }}
              >
                {groupLabel}:
              </ListSubheader>,
              ...groupOptions.map(([value, label]) => (
                <MenuItem key={`${groupLabel}-${value}`} value={value}>
                  {label || "\u00A0"}
                </MenuItem>
              )),
            ])
        ) : (
          options.map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label || "\u00A0"}
            </MenuItem>
          ))
        )}
      </Select>
      {typeof error === "string" ? (
        <FormHelperText error sx={{ whiteSpace: "nowrap" }}>
          {error}
        </FormHelperText>
      ) : warning ? (
        <FormHelperText sx={{ display: "flex", color: "warning.main" }}>
          <WarningAmberRoundedIcon sx={{ width: "16px", height: "16px", pr: "5px" }} />
          {warning}
        </FormHelperText>
      ) : helperText ? (
        <FormHelperText>{helperText}</FormHelperText>
      ) : null}
    </FormControl>
  );
}
